import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CanvasPath } from 'react-sketch-canvas/src/types';
import { CanvasHandle } from '../../../components/Canvas';
import { useParts } from '../../../hooks/common/useParts';
import { Direction, getByOrdinal, Side } from '../../../utils/enum';
import {
  dataURItoBlob,
  mergePictures,
  uploadImage,
} from '../UploadDocumentPage/utils';
import { InfoMessage } from '../../../components/InfoMessage';
import { FreehandBodyDiagramInput } from './FreehandBodyDiagramInput';
import { Button } from '../../../components/Button';
import * as api from '../../../api';
import styles from './index.module.css';

const EXPORT_IMAGE_WIDTH: number = 600;
const EXPORT_IMAGE_HEIGHT: number = 1200;

interface EditViewProps {
  onComplete?: (mergedDataUri: string) => Promise<void> | void;
  disabled?: boolean;
  brushColor?: string;
}

export const FreehandBodyDiagramPageEditView: React.FC<EditViewProps> = ({
  onComplete,
  disabled,
  brushColor,
}) => {
  const { t } = useTranslation();
  const [frontImageDataUrl, setFrontImageDataUrl] = useState<string>();
  const [backImageDataUrl, setBackImageDataUrl] = useState<string>();
  const [frontImagePaths, setFrontImagePaths] = useState<CanvasPath[]>([]);
  const [backImagePaths, setBackImagePaths] = useState<CanvasPath[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>();
  const [pending, setPending] = useState(false);
  const ref = useRef<CanvasHandle | null>(null);
  const { part, onNext } = useParts({
    maxParts: 2,
    resetOnMount: true,
  });
  const side = getByOrdinal(Side, part - 1);

  const onSideComplete = async (side: Side) => {
    setPending(true);
    const imageDataUrl = await ref.current?.exportImage(
      EXPORT_IMAGE_WIDTH,
      EXPORT_IMAGE_HEIGHT,
    );
    const paths = await ref.current?.exportPaths();
    setPending(false);
    if (!imageDataUrl || !paths) {
      setErrorMessage('Sorry something went wrong when saving the image data!');
      return false;
    }
    if (side === Side.Front) {
      setFrontImageDataUrl(imageDataUrl);
      setFrontImagePaths(paths);
    } else {
      setBackImageDataUrl(imageDataUrl);
      setBackImagePaths(paths);
    }
    return true;
  };

  useEffect(() => {
    if (frontImageDataUrl && backImageDataUrl) {
      const onAllPageComplete = async (
        frontImageDataUrl: string,
        backImageDataUrl: string,
      ) => {
        setPending(true);
        try {
          const merged = await mergePictures({
            frontDataUri: frontImageDataUrl,
            backDataUri: backImageDataUrl,
            direction: Direction.Horizontal,
            resize: false,
            padding: 0,
          });
          onComplete && (await onComplete(merged));
        } catch (e) {
          setErrorMessage('Something went wrong, please try again later.');
        } finally {
          setPending(false);
        }
      };
      onAllPageComplete(frontImageDataUrl, backImageDataUrl);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [frontImageDataUrl, backImageDataUrl]);

  return (
    <div className={styles.EditView}>
      {errorMessage && (
        <InfoMessage isError={true}>{t(errorMessage)}</InfoMessage>
      )}
      <FreehandBodyDiagramInput
        key={side}
        ref={ref}
        side={side}
        defaultPaths={side === Side.Front ? frontImagePaths : backImagePaths}
        brushColor={brushColor}
      />
      <Button
        type="button"
        className={styles.PrimaryButton}
        children={
          Side.Front === side ? t('Continue to back of body') : t('Continue')
        }
        disabled={pending || disabled}
        onClick={async () => {
          if (await onSideComplete(side)) onNext();
        }}
      />
    </div>
  );
};

interface EditViewContainerProps {
  navigateToNextPage: () => Promise<void>;
  field: api.PrevisitFormField.PrevisitFormFieldWithConfig;
  setFormField: (formFieldId: string, value: string) => void;
  encounter: api.Encounter.Encounter;
  brushColor?: string;
}

export const FreehandBodyDiagramPageEditViewContainer: React.FC<
  EditViewContainerProps
> = ({ navigateToNextPage, field, setFormField, encounter, brushColor }) => {
  const [pending, setPending] = useState(false);
  const onComplete = async (mergedDataUri: string) => {
    setPending(true);
    const blob = dataURItoBlob(mergedDataUri);
    await uploadImage({
      documentType: api.PatientUpload.DocumentType.FreehandBodyDiagram,
      imageBlob: blob,
      encounterId: encounter.id,
      onUploaded: async (uploadId: string) => {
        await api.client.Previsit.updateFormField(
          field.previsitFormSetId,
          field.id,
          uploadId,
        );
        setFormField(field.id, uploadId);
        await navigateToNextPage();
        setPending(false);
      },
      rethrow: true,
    });
  };
  return (
    <FreehandBodyDiagramPageEditView
      onComplete={onComplete}
      disabled={pending}
      brushColor={brushColor}
    />
  );
};
